import React, { useEffect, useState } from 'react';
import './App.css';
import { LicenseInfo } from '@mui/x-license';
import { useAuth } from 'react-oidc-context';
import { BrowserRouter as Router, useSearchParams } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CreateApi from './Settings/appSettings';
import { useLSContext } from './Context/useLSContext';
import { setAppTheme } from './Settings/appTheme';
import { isUpdateAvailable } from './utils/serviceWorkerRegistration';
import { useCache } from './Context/CacheContext';
import { createCookie, getCookie, deleteCookie } from "./Context/CookieContext";
import { useDataContext } from './Context/useDataContext';
import { useHoleSettings } from './Context/useHoleSettingsContext';
import { Leaderboard } from './DOM/Views/Leaderboard';
import { LBHeader } from './DOM/Components/LBHeader';
import { EventSelector } from './DOM/Components/EventSelector';

if (process.env.REACT_APP_MUI_X_LICENSE_KEY) {
  LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_X_LICENSE_KEY);
}

function AppContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const api = CreateApi();
  const { factor, appTheme, setTheme, windowSize, setWindowSize, isHeadless, setIsHeadless, setHideSelector } = useLSContext();
  const { eventData, setEventData } = useDataContext();
  const [availableEvents, setAvailableEvents] = useState<any>([]);

  useEffect(() => {
    setTheme(setAppTheme(factor));
  }, [factor]);

  useEffect(() => {
    fetchAvailableEvents();
  }, []);

  // Check for headless mode from URL parameter and update context
  useEffect(() => {
    const headlessParam = searchParams.get('headless') === 'true';
    setIsHeadless(headlessParam);
  }, [searchParams, setIsHeadless]);

  // Check for hideSelector mode from URL parameter and update context
  useEffect(() => {
    const hideSelectorParam = searchParams.get('hideSelector') === 'true';
    setHideSelector(hideSelectorParam);
  }, [searchParams, setHideSelector]);

  // Deep linking: pre-select event from eventID URL parameter (works even if event isn't public)
  const eventIDParam = searchParams.get('eventID') || searchParams.get('eventId');
  useEffect(() => {
    if (!eventIDParam) return;
    // Already showing this event (e.g. from prior fetch by ID)
    if (eventData?._id === eventIDParam) return;
    if (availableEvents.length > 0) {
      const event = availableEvents.find((e: any) => e._id === eventIDParam);
      if (event) {
        setEventData(event);
        return;
      }
    }
    // Event not in available list (e.g. not public) — fetch by direct link
    const fetchEventById = async () => {
      try {
        const response = await api.get('/lb/GetEventById', { params: { eventID: eventIDParam } });
        if (response.data) setEventData(response.data);
      } catch (err) {
        console.warn('Direct link event not found or failed to load:', eventIDParam, err);
      }
    };
    fetchEventById();
  }, [eventIDParam, availableEvents, eventData?._id, setEventData]);

  // Auto-select the first available event when autoSelectFirst=true and no eventID is specified
  const autoSelectFirst = searchParams.get('autoSelectFirst') === 'true';
  useEffect(() => {
    if (!autoSelectFirst || eventIDParam) return;
    if (!availableEvents?.length || eventData?._id) return;
    const first = availableEvents[0];
    setEventData(first);
    const params = new URLSearchParams(searchParams);
    if (first?._id) params.set('eventID', first._id);
    setSearchParams(params, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSelectFirst, eventIDParam, availableEvents, eventData?._id]);

  // Update appBarHeight based on headless mode
  useEffect(() => {
    setWindowSize((prev: any) => ({
      ...prev,
      appBarHeight: isHeadless ? 0 : 70
    }));
  }, [isHeadless, setWindowSize]);

  const fetchAvailableEvents = async () => {
    const response = await api.get('/lb/GetAvailableEvents');
    console.log('Available Events:', response.data);
    setAvailableEvents(response.data);
  }

  return (
    <div className="App leaderboard" style={{ 
      height: '100%',
      width: '100%',
      //minHeight: '100dvh',
      //minWidth: '100dvw',
      overflow: 'hidden',
      position: 'absolute',
      paddingBottom: 'env(safe-area-inset-bottom)',
      paddingTop: 'env(safe-area-inset-top)',
      WebkitOverflowScrolling: 'touch',
      overscrollBehavior: 'none',
      backgroundColor: 'rgb(253, 252, 248)',
      //border: '1px solid red'
    }}>
      <ThemeProvider theme={appTheme}>
        {!isHeadless && (
          <LBHeader availableEvents={availableEvents} eventData={eventData} setEventData={setEventData} />
        )}
        <Leaderboard availableEvents={availableEvents} />
      </ThemeProvider>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
